import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import {
  compose,
  insertItem,
  patch,
  removeItem,
  updateItem,
} from '@ngxs/store/operators';
import { DataModuleWithRelations } from '@osos/shared/api';
import {
  BreadcrumbToken,
  DataModulePresenterModel,
  DEFAULT_PAGESIZE,
  LoadingIndicator,
  Page,
  Where,
} from '@osos/shared/types';
import { EMPTY, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { DataModuleFactoryService } from '../../factories/data-module-factory/data-module-factory.service';
import {
  ApiUtilsService,
  BreadcrumbService,
  ModuleDataService,
} from '../../services';
import { LoadingActions } from '../loading/loading.actions';
import { NavigationStateActions } from '../navigation/navigation.actions';
import { ModuleActions } from './module.action';

export interface ModuleStateModel {
  page: Page<DataModuleWithRelations>;
  presentedModules?: DataModulePresenterModel[];
  presentedModule: { [key: string]: DataModulePresenterModel };
  selectedModule?: string;
  /**
   * temporary state to store modules and select them once
   */
  transitoryModules: DataModulePresenterModel[];
  loading: LoadingIndicator;
}

@State<ModuleStateModel>({
  name: 'dataModules',
  defaults: {
    page: {
      items: [],
      totalCount: 0,
    },
    presentedModule: {},
    presentedModules: [],

    transitoryModules: [],
    loading: new LoadingIndicator(),
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class ModuleState {
  constructor(
    private dataModulesService: ModuleDataService,
    private apiHelper: ApiUtilsService,
    private dataModulesFactoryService: DataModuleFactoryService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------

  @Selector([ModuleState])
  static selectedModule(state: ModuleStateModel): DataModulePresenterModel {
    return state.presentedModule[state.selectedModule];
  }

  @Selector([ModuleState])
  static totalCount(state: ModuleStateModel): number {
    return state.page.totalCount;
  }

  @Selector([ModuleState])
  static presentedModules(state: ModuleStateModel): DataModulePresenterModel[] {
    return state.presentedModules;
  }

  @Selector([ModuleState])
  /**
   * select transitory modules.
   * it's valid to be readed once after trigger TransitoryFetchModules action
   */
  static transitoryModules(
    state: ModuleStateModel,
  ): DataModulePresenterModel[] {
    return state.transitoryModules;
  }

  static presentedModule(
    id: string,
  ): (state: ModuleStateModel) => DataModulePresenterModel {
    return createSelector(
      [ModuleState],
      (state: ModuleStateModel) => state.presentedModule[id],
    );
  }

  // --------------------------------- ACTIONS --------------------------------

  @Action(ModuleActions.SelectModule)
  selectModule(
    { getState, patchState }: StateContext<ModuleStateModel>,
    { moduleId }: ModuleActions.SelectModule,
  ): void {
    const selectedModule = getState().presentedModule[moduleId];
    patchState({
      selectedModule: moduleId,
    });

    if (selectedModule) {
      this.breadcrumbService.set(
        BreadcrumbToken?.ModuleName,
        selectedModule?.name,
      );
    }
  }

  @Action(ModuleActions.FetchModule)
  fetchModule(
    { setState, getState }: StateContext<ModuleStateModel>,
    { moduleId }: ModuleActions.FetchModule,
  ): Observable<DataModuleWithRelations> {
    const module = getState().presentedModule[moduleId];

    if (module) {
      return EMPTY;
    }

    setState(
      patch<ModuleStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.dataModulesService.findById({ id: moduleId }).pipe(
      tap((res) => {
        const presenter =
          this.dataModulesFactoryService.createPresenterModel(res);
        setState(
          patch<ModuleStateModel>({
            presentedModule: patch({
              [moduleId]: presenter,
            }),
          }),
        );
      }),
      finalize(() => {
        setState(
          patch<ModuleStateModel>({
            loading: patch<LoadingIndicator>({
              item: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(ModuleActions.FetchModules, { cancelUncompleted: true })
  fetchModules(
    { setState, patchState, dispatch }: StateContext<ModuleStateModel>,
    {
      payload: { skip, limit, searchTerm, includeCoreModule },
    }: ModuleActions.FetchModules,
  ): Observable<Page<DataModuleWithRelations>> {
    dispatch(new LoadingActions.SetLoading(true));

    setState(
      patch<ModuleStateModel>({
        presentedModules: [],
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    const where: Where<DataModuleWithRelations> = {
      or: this.apiHelper.prepareSearch(searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
    };

    return this.dataModulesService
      .find({
        filter: this.apiHelper.prepareFilter<DataModuleWithRelations>({
          where,
          skip: skip ?? 0,
          limit: limit ?? DEFAULT_PAGESIZE,
          order: ['name.en DESC'],
        }),
        includeCoreModule: includeCoreModule,
      })
      .pipe(
        switchMap((elements) =>
          this.dataModulesService
            .count(this.apiHelper.prepareWhere<DataModuleWithRelations>(where))
            .pipe(
              map((count) => {
                return {
                  items: elements,
                  totalCount: count.count,
                  pageSize: limit ?? DEFAULT_PAGESIZE,
                } as Page<DataModuleWithRelations>;
              }),
            ),
        ),
        tap((page) => {
          const presentedModules = page.items.map((x) =>
            this.dataModulesFactoryService.createPresenterModel(x),
          );

          patchState({
            page,
            presentedModules,
          });
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
          setState(
            patch<ModuleStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ModuleActions.TransitoryFetchModules)
  transitoryFetchModules(
    ctx: StateContext<ModuleStateModel>,
    {
      payload: { skip, limit, searchTerm, moduleIds, includeCoreModule },
    }: ModuleActions.TransitoryFetchModules,
  ): Observable<Page<DataModuleWithRelations>> {
    const where: Where<DataModuleWithRelations> = {
      id: moduleIds.length ? { inq: moduleIds } : undefined,
      or: this.apiHelper.prepareSearch(searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
    };

    return this.dataModulesService
      .find({
        filter: this.apiHelper.prepareFilter<DataModuleWithRelations>({
          where,
          skip: skip ?? 0,
          limit: limit ?? DEFAULT_PAGESIZE,
          order: ['name.en DESC'],
        }),
        includeCoreModule: includeCoreModule,
      })
      .pipe(
        switchMap((elements) =>
          this.dataModulesService
            .count(this.apiHelper.prepareWhere<DataModuleWithRelations>(where))
            .pipe(
              map((count) => {
                return {
                  items: elements,
                  totalCount: count.count,
                } as Page<DataModuleWithRelations>;
              }),
            ),
        ),
        tap((page) => {
          const presentedDataModules = page.items.map((x) =>
            this.dataModulesFactoryService.createPresenterModel(x),
          );
          ctx.patchState({
            transitoryModules: presentedDataModules,
          });
        }),
      );
  }

  @Action(ModuleActions.DeleteModule)
  deleteModule(
    { setState, dispatch }: StateContext<ModuleStateModel>,
    { moduleId }: ModuleActions.DeleteModule,
  ): Observable<void> {
    setState(
      patch<ModuleStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    return this.dataModulesService.deleteById({ id: moduleId }).pipe(
      tap(() => {
        setState(
          patch<ModuleStateModel>({
            page: patch<Page<DataModuleWithRelations>>({
              items: removeItem((i) => i.id === moduleId),
              totalCount: (c) => c--,
            }),
            presentedModules: removeItem((i) => i.id === moduleId),
            presentedModule: {
              [moduleId]: undefined,
            },
          }),
        );
        // remove module from sidebar:
        dispatch(new NavigationStateActions.RemoveNavigationItem(moduleId));
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetCommandLoading(false));

        setState(
          patch<ModuleStateModel>({
            loading: patch<LoadingIndicator>({
              remove: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(ModuleActions.CreateModule)
  createModule(
    { setState, getState, dispatch }: StateContext<ModuleStateModel>,
    { module }: ModuleActions.CreateModule,
  ): Observable<DataModuleWithRelations> {
    setState(
      patch<ModuleStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    return this.dataModulesService
      .create({
        body: this.dataModulesFactoryService.createDataModel(module),
      })
      .pipe(
        tap((res) => {
          const presenter =
            this.dataModulesFactoryService.createPresenterModel(res);
          const pageSize = getState().page.pageSize;
          setState(
            patch<ModuleStateModel>({
              presentedModules: compose(
                insertItem(presenter),
                removeItem(pageSize),
              ),
              page: patch<Page<DataModuleWithRelations>>({
                items: insertItem(res),
                totalCount: (c) => c++,
              }),
            }),
          );
        }),
        finalize(() => {
          //fetch sidebar items to reflect new added module
          dispatch(new LoadingActions.SetCommandLoading(false));

          dispatch(new NavigationStateActions.FetchItemsPinnedToSidebarV2());
          setState(
            patch<ModuleStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ModuleActions.UpdateModule)
  updateModule(
    { setState, dispatch }: StateContext<ModuleStateModel>,
    { module }: ModuleActions.UpdateModule,
  ): Observable<DataModuleWithRelations> {
    setState(
      patch<ModuleStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    return this.dataModulesService
      .updateById({
        id: module.id,
        body: this.dataModulesFactoryService.createDataModel(module),
      })
      .pipe(
        switchMap(() => this.dataModulesService.findById({ id: module.id })),
        tap((res) => {
          const presenter =
            this.dataModulesFactoryService.createPresenterModel(res);

          setState(
            patch<ModuleStateModel>({
              presentedModules: updateItem(
                (i) => i.id === module.id,
                presenter,
              ),
              page: patch<Page<DataModuleWithRelations>>({
                items: updateItem<DataModuleWithRelations>(
                  (m) => m.id === module.id,
                  { ...res, id: module.id },
                ),
              }),
              presentedModule: patch({
                [module.id]: presenter,
              }),
            }),
          );

          // Update pinned module in sidebar after update module data
          dispatch(
            new NavigationStateActions.UpdateNavigationItem({
              id: module.id,
              name: presenter.name,
              icon: presenter.icon,
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ModuleStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }
}
